/**
 * /blog - blog feed (Editorial aesthetic).
 *
 * Tap chi luat co dien: masthead day kep, featured hero pull-quote, cluster
 * heading small-caps + ornamental rule, asymmetric editorial grid (lead story
 * span 7 / secondary span 5 / rest span 4). Paper-grain texture lop nen.
 *
 * SEO:
 *   - title "Blog phap luat - Asklaw"
 *   - canonical https://asklaw.vn/blog
 *   - JSON-LD ItemList of all posts
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadAllPosts, clusterLabel, type BlogPost } from '../lib/blog-loader';
import { useLang } from '../use-lang';

function isEnPost(p: BlogPost): boolean {
  // lang la source of truth (loader default 'vi' -> luon defined). Fallback
  // cluster chi cho truong hop lang undefined (defensive). Truoc day chi check
  // cluster==='expat-en' -> sot 3 bai cluster 've-asklaw-en' co lang='en'.
  if (p.lang) return p.lang === 'en';
  return p.cluster === 'expat-en' || p.cluster === 've-asklaw-en';
}

const SITE_ORIGIN = 'https://asklaw.vn';
const PAGE_SIZE = 20;

export function BlogFeedRoute() {
  const { lang } = useLang();
  const [allPosts, setAllPosts] = useState<BlogPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    loadAllPosts()
      .then((all) => setAllPosts(all))
      .catch((e: Error) => setError(e.message));
  }, []);

  const posts = useMemo(() => {
    if (!allPosts) return null;
    const preferEn = lang === 'en';
    const primary = allPosts.filter((p) => isEnPost(p) === preferEn);
    const secondary = allPosts.filter((p) => isEnPost(p) !== preferEn);
    return [...primary, ...secondary];
  }, [allPosts, lang]);

  useEffect(() => {
    document.title = 'Blog pháp luật - Asklaw';
    setMeta('description', 'Bài viết pháp luật Việt Nam: lao động, đất đai, hôn nhân, doanh nghiệp. Trích dẫn Điều/Khoản chính xác từ vbpl.moj.gov.vn.');
    setCanonical(`${SITE_ORIGIN}/blog`);
    setMeta('og:title', 'Blog pháp luật - Asklaw', true);
    setMeta('og:description', 'Bài viết pháp luật Việt Nam có trích dẫn chính xác', true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', `${SITE_ORIGIN}/blog`, true);
    setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large');
  }, []);

  const featured = posts && posts.length > 0 ? posts[0]! : null;
  const rest = posts ? posts.slice(1) : [];

  const grouped = useMemo(() => {
    const m = new Map<string, BlogPost[]>();
    for (const p of rest.slice(0, visible - 1)) {
      const arr = m.get(p.cluster) ?? [];
      arr.push(p);
      m.set(p.cluster, arr);
    }
    return m;
  }, [rest, visible]);

  const jsonLdItemList = useMemo(() => {
    if (!posts) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: posts.slice(0, visible).map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${SITE_ORIGIN}/blog/${p.slugSegment}`,
        name: p.title,
      })),
    };
  }, [posts, visible]);

  useEffect(() => {
    if (!jsonLdItemList) return;
    injectJsonLd('blog-feed-itemlist', jsonLdItemList);
    return () => removeJsonLd('blog-feed-itemlist');
  }, [jsonLdItemList]);

  if (error) {
    return (
      <main className="blog-feed">
        <h1>Lỗi tải blog</h1>
        <p>{error}</p>
      </main>
    );
  }

  if (!posts) {
    return (
      <main className="blog-feed">
        <p style={{ color: 'var(--asklaw-muted)' }}>Đang tải...</p>
      </main>
    );
  }

  if (posts.length === 0) {
    return (
      <main className="blog-feed">
        <h1>Blog pháp luật</h1>
        <p>Chưa có bài viết. Quay lại sau.</p>
      </main>
    );
  }

  const issueNumber = posts.length;
  const today = new Date().toLocaleDateString('vi-VN', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <main className="blog-feed">
      <nav aria-label="Breadcrumb" className="bf-breadcrumb">
        <Link to="/">Trang chủ</Link>
        <span className="bf-breadcrumb-sep">/</span>
        <span aria-current="page">Blog</span>
      </nav>

      <header className="bf-masthead">
        <div className="bf-masthead-meta">Số {String(issueNumber).padStart(2, '0')}</div>
        <h1 className="bf-masthead-title">Blog pháp luật</h1>
        <div className="bf-masthead-meta bf-masthead-meta--right">{today}</div>
        <p className="bf-masthead-tagline">
          Bài viết có trích dẫn Điều và Khoản chính xác từ văn bản pháp luật hiệu lực
        </p>
      </header>

      {featured ? <FeaturedHero post={featured} /> : null}

      {Array.from(grouped.entries()).map(([cluster, items]) => (
        <section key={cluster} className="bf-cluster" aria-labelledby={`cluster-${cluster}`}>
          <div className="bf-cluster-head">
            <h2 id={`cluster-${cluster}`} className="bf-cluster-label">
              {clusterLabel(cluster)}
            </h2>
            <div className="bf-cluster-rule" aria-hidden="true" />
            <span className="bf-cluster-count">
              {String(items.length).padStart(2, '0')} bài
            </span>
          </div>
          <ul className="bf-cluster-grid">
            {items.map((p, idx) => (
              <li key={p.slugSegment}>
                <StoryCard post={p} folio={idx + 1} />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {posts.length > visible ? (
        <div className="bf-loadmore-wrap">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PAGE_SIZE)}
            className="bf-loadmore"
          >
            Tải thêm bài viết
          </button>
        </div>
      ) : null}
    </main>
  );
}

function FeaturedHero({ post }: { post: BlogPost }) {
  const pullQuote = extractPullQuote(post);
  return (
    <section className="bf-featured" aria-label="Bài nổi bật">
      <div>
        <div className="bf-featured-rail">
          <strong>Hôm nay</strong> · {clusterLabel(post.cluster)}
        </div>
        <h2 className="bf-featured-title">
          <Link to={`/blog/${post.slugSegment}`}>{post.title}</Link>
        </h2>
        <p className="bf-featured-deck">{post.description}</p>
        <div className="bf-featured-meta">
          <span><b>{formatDate(post.publish_date)}</b></span>
          <span>{post.readMinutes} phút đọc</span>
          {post.citations_count ? <span>{post.citations_count} trích dẫn</span> : null}
          <span>Tác giả · {post.author}</span>
        </div>
      </div>
      <aside className="bf-pullquote">
        <p className="bf-pullquote-body">{pullQuote.body}</p>
        <div className="bf-pullquote-cite">— {pullQuote.cite}</div>
      </aside>
    </section>
  );
}

function StoryCard({ post, folio }: { post: BlogPost; folio: number }) {
  return (
    <Link to={`/blog/${post.slugSegment}`} className="bf-story">
      <span className="bf-story-folio">№ {String(folio).padStart(2, '0')}</span>
      <h3 className="bf-story-title">{post.title}</h3>
      <p className="bf-story-deck">
        {post.description.length > 180 ? post.description.slice(0, 180) + '…' : post.description}
      </p>
      <div className="bf-story-meta">
        <span><b>{formatDate(post.publish_date)}</b></span>
        <span>{post.readMinutes} phút đọc</span>
        {post.citations_count ? <span>{post.citations_count} trích dẫn</span> : null}
      </div>
    </Link>
  );
}

/**
 * Best-effort pull-quote: first sentence from description that mentions a
 * legal article (Dieu/Khoan/Luat/Nghi dinh), else first sentence of description.
 * No silent failure — caller always gets a quote.
 */
function extractPullQuote(post: BlogPost): { body: string; cite: string } {
  const text = post.description.replace(/\s+/g, ' ').trim();
  const sentences = text.split(/(?<=[.!?])\s+/).filter((s) => s.length > 20);
  const legal = sentences.find((s) =>
    /Điều\s+\d+|Khoản\s+\d+|Luật\s+|Nghị\s+định|Bộ luật|\d+\/\d{4}\/[A-ZĐ]/i.test(s),
  );
  const pick = legal ?? sentences[0] ?? text;
  const body = pick.length > 180 ? pick.slice(0, 178) + '…' : pick;
  const cite = post.citations_count
    ? `${clusterLabel(post.cluster)} · ${post.citations_count} trích dẫn`
    : clusterLabel(post.cluster);
  return { body, cite };
}

function formatDate(iso: string): string {
  // Expect YYYY-MM-DD; render as DD/MM/YYYY (VN convention).
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string): void {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(id: string, obj: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) el.remove();
}
